import { Link, useNavigate } from 'react-router';

import { routes } from '@/assets/constants/routes-no.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { useAuth } from '@/services/Auth/index.ts';
import { Button } from '@/ui/button-2.tsx';

import { Section } from '../section/index.ts';

import cardCreditScoreSrc from './card-credit-score.svg';
import cardDebtCheckSrc from './card-debt-check.svg';
import cardInterestRateSrc from './card-interest-rate.svg';
import cardPropertyValueSrc from './card-property-value.svg';

export const EconomySection = () => {
  const { login, isAnonymous } = useAuth();
  const navigate = useNavigate();

  const loginAction = async () => {
    if (isAnonymous) {
      await login('SCRIVE', routes.MY_SCOOPR);
    } else {
      await navigate(routes.MY_SCOOPR);
    }
  };

  return (
    <Section.Container className="bg-[#18A0FB] text-white not-first:mt-8 not-last:mb-8">
      <Section.Content className="flex flex-col py-10">
        <h2 className="mb-8 text-center typography-v1-h2 text-pretty">
          Ta kontroll over økonomien din
        </h2>

        <ul className="mx-auto mb-8 grid grid-cols-1 gap-4 max-sm:w-full max-sm:max-w-sm sm:mb-10 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
          <ProductCard
            imageSrc={cardCreditScoreSrc}
            title="Kredittsjekk deg selv"
            linkTo={routes.MY_SCOOPR}
            onClick={async (e) => {
              sendUserPortalEvent('economy_section_credit_score_clicked');
              e.preventDefault();
              await loginAction();
            }}
          />
          <ProductCard
            imageSrc={cardDebtCheckSrc}
            title="Sjekk din gjeld"
            linkTo={routes.MY_DEBT}
            onClick={() => {
              sendUserPortalEvent('economy_section_debt_check_clicked');
            }}
          />
          <ProductCard
            imageSrc={cardInterestRateSrc}
            title="Sammenlign din rente"
            linkTo={routes.PRODUCTS_REFINANCING}
            onClick={async (e) => {
              sendUserPortalEvent('economy_section_interest_rate_clicked');
              e.preventDefault();
              await loginAction();
            }}
          />
          <ProductCard
            imageSrc={cardPropertyValueSrc}
            title="Sjekk din boligverdi"
            linkTo={routes.PROPERTY_VALUE}
            onClick={() => {
              sendUserPortalEvent('economy_section_property_value_clicked');
            }}
          />
        </ul>

        <Button
          color="yellow"
          className="mx-auto"
          action={async () => {
            sendUserPortalEvent('economy_section_cta_clicked');
            await loginAction();
          }}
        >
          Få oversikt
        </Button>
      </Section.Content>
    </Section.Container>
  );
};

const ProductCard = (props: {
  imageSrc: string;
  title: string;
  linkTo: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) => {
  return (
    <li className="flex max-w-sm gap-4 rounded-2xl bg-white p-2 text-left text-v1-background-200 max-sm:h-20 sm:flex-col sm:gap-6 sm:rounded-3xl sm:p-4 sm:text-center">
      <div style={{ aspectRatio: '310 / 167' }} className="w-auto overflow-hidden rounded-xl">
        <img src={props.imageSrc} width={310} height={167} alt="" />
      </div>
      <div className="flex flex-1 flex-col items-start justify-center sm:items-center">
        <h3 className="mb-1 typography-v1-h4 sm:mb-2 sm:typography-v1-h3">{props.title}</h3>
        <Link
          to={props.linkTo}
          onClick={props.onClick}
          className="inline-flex cursor-pointer items-center justify-center gap-2 typography-v1-body4 text-[#18A0FB] hover:underline"
        >
          Sjekk nå{' '}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 7 12"
            className="-mt-px h-[0.8em] w-auto shrink-0"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 1 5 5-5 5"
            />
          </svg>
        </Link>
      </div>
    </li>
  );
};
